import {AfterViewInit, Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../api-request.service";
import {UtilsService} from "../utils.service";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {ApiDataSource} from "../../utils/api-data-source";
import {
  SafetyObservationsSelectorFilterComponent
} from "../safety-observations-selector-filter/safety-observations-selector-filter.component";

@Component({
  selector: 'app-safety-observations-selector',
  templateUrl: './safety-observations-selector.component.html',
  styleUrls: ['./safety-observations-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SafetyObservationsSelectorComponent implements OnInit, AfterViewInit {

  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'so_description',
    'so_category',
    'so_type',
    'so_status',
    'site',
    'date_created'
  ];
  dataSource: SafetyObservationsSelectorDataSource;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public dialogRef: MatDialogRef<SafetyObservationsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    // Set multiple to false if it is not present.
    if (typeof this.data['multiple'] === 'undefined') {
      this.data['multiple'] = false;
    }

    // Set required to false if it is not present.
    if (typeof this.data['required'] === 'undefined') {
      this.data['required'] = false;
    }

    // Set hide_assets to false if it is not present.
    if (typeof this.data['hide_assets'] === 'undefined') {
      this.data['hide_assets'] = false;
    }

    // Construct the data source.
    this.dataSource = new SafetyObservationsSelectorDataSource(this.app, this.api, this.data['multiple']);

    // Apply the Asset visibility filter.
    this.dataSource.hide_assets = this.data['hide_assets'];

    // Default selection to nothing when no selected values are provided.
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // Preselect the provided values.
    for (let i = 0; i < this.data['selected'].length; i++) {
      if (this.data['selected'][i]) {
        this.dataSource.selection.select(this.data['selected'][i]);
      }
    }

    // Scope the list to a specific site if provided.
    if (this.data['site_id']) {
      this.dataSource.site_ids = [this.data['site_id']];
    }
  }

  ngOnInit() {
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.dataSource.getData(false);
    });

    // Subscribe to the paginator and sorter tap events.
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Clear the current selection.
   *
   * @return {void}
   */
  onClearSelected(): void {
    this.dataSource.selection.clear();
  }

  /**
   * Open the filter dialog and apply the selected filters.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      SafetyObservationsSelectorFilterComponent,
      {
        so_category: this.dataSource.so_category,
        so_type: this.dataSource.so_type,
        so_priority: this.dataSource.so_priority,
        so_status: this.dataSource.so_status,
        so_rating: this.dataSource.so_rating,
        site_ids: this.dataSource.site_ids,
        contractor_ids: this.dataSource.contractor_ids,
        user_ids: this.dataSource.user_ids,
        asset_ids: this.dataSource.asset_ids,
        reported_by_ids: this.dataSource.reported_by_ids,
        date_filter_range: this.dataSource.date_filter_range,
        hide_assets: this.dataSource.hide_assets
      },
      {
        width: '900px'
      },
      (results) => {
        if (typeof results === 'undefined') { return; }

        this.dataSource.so_category = results.so_category ?? '';
        this.dataSource.so_type = results.so_type ?? '';
        this.dataSource.so_priority = results.so_priority ?? '';
        this.dataSource.so_status = results.so_status ?? '';
        this.dataSource.so_rating = results.so_rating ?? '';
        this.dataSource.site_ids = results.site_ids ?? [];
        this.dataSource.contractor_ids = results.contractor_ids ?? [];
        this.dataSource.user_ids = results.user_ids ?? [];
        this.dataSource.asset_ids = results.asset_ids ?? [];
        this.dataSource.reported_by_ids = results.reported_by_ids ?? [];
        this.dataSource.date_filter_range = results.date_filter_range ?? [];

        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Save the selected data and close the dialog.
   *
   * @return {void}
   */
  onSaveAndClose(): void {
    let response: number | number[];

    if (this.data['multiple']) {
      response = this.dataSource.selection.selected;
    } else {
      response = this.dataSource.selection.selected.length ? this.dataSource.selection.selected[0] : 0;
    }

    this.dialogRef.close(response);
  }

  /**
   * Close the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }
}

export class SafetyObservationsSelectorDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';
  site_ids: number[] = [];

  // Filter options (mirrors the main Safety Observations list).
  so_category: string = '';
  so_type: string = '';
  so_priority: string = '';
  so_status: string = '';
  so_rating: string = '';
  contractor_ids: number[] = [];
  user_ids: number[] = [];
  asset_ids: number[] = [];
  reported_by_ids: number[] = [];
  date_filter_range: Date[] = [];
  hide_assets: boolean = false;

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/safety-observations', resetOffset, {
      site_ids: this.site_ids.length ? this.site_ids.join(',') : '',
      so_category: this.so_category || '',
      so_type: this.so_type || '',
      so_priority: this.so_priority || '',
      so_status: this.so_status || '',
      so_rating: this.so_rating || '',
      contractor_ids: this.contractor_ids.length ? this.contractor_ids.join(',') : '',
      user_ids: this.user_ids.length ? this.user_ids.join(',') : '',
      asset_ids: this.asset_ids.length ? this.asset_ids.join(',') : '',
      reported_by_ids: this.reported_by_ids.length ? this.reported_by_ids.join(',') : '',
      date_filter_range: this.date_filter_range && this.date_filter_range.length > 1 ? [
        this.date_filter_range[0].getTime() / 1000,
        this.date_filter_range[1].getTime() / 1000
      ].join(',') : ''
    });
  }
}
